import React from 'react';
import { Helmet } from 'react-helmet';

const defaultMeta = {
  title: 'Neevii — AI Accounting that does the boring bits',
  description: 'Upload receipts, categorize transactions, send invoices — in minutes. Try the AI accountant today.',
  ogType: 'website',
};

const CANONICAL_BASE_URL = 'https://www.neevii.com';
const OG_IMAGE_URL = 'https://www.neevii.com/assets/images/neevii-og-image.png';
const GOOGLE_SITE_VERIFICATION = import.meta.env.VITE_GOOGLE_SITE_VERIFICATION;

// Organization structured data (JSON-LD)
const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Neevii',
  url: 'https://www.neevii.com',
  logo: 'https://www.neevii.com/assets/images/Neevii_logo.png',
  description: 'AI-powered accounting software for freelancers, sole traders, and small businesses in the UK.',
  email: 'support@neevii.com',
  sameAs: [
    'https://twitter.com/neabordeaux'
  ]
};

// SoftwareApplication structured data
const softwareSchema = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'Neevii',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  description: 'AI-powered accounting software that automates expense categorization, tax filing, and invoicing.',
  offers: {
    '@type': 'AggregateOffer',
    priceCurrency: 'GBP',
    lowPrice: '9',
    highPrice: '79',
    offerCount: '4'
  },
  featureList: [
    'AI-assisted expense categorization',
    'Self Assessment (SA100) filing',
    'Corporation Tax (CT600) filing',
    'VAT returns',
    'Invoice creation and PDF exports',
    'AI Accountant Chat powered by Google Gemini'
  ]
};

function getCanonicalUrl() {
  if (typeof window === 'undefined') {
    return `${CANONICAL_BASE_URL}/`;
  }
  const path = window.location.pathname || '/';
  return `${CANONICAL_BASE_URL}${path}`;
}

export default function Seo({ title, description, og = {}, schema = null }) {
  const meta = {
    ...defaultMeta,
    title: title || defaultMeta.title,
    description: description || defaultMeta.description,
    ...og,
  };

  // Combine schemas: always include org, optionally add software on homepage, plus any custom
  const schemas = [organizationSchema];
  if (typeof window !== 'undefined' && window.location.pathname === '/') {
    schemas.push(softwareSchema);
  }
  if (schema) {
    schemas.push(schema);
  }

  return (
    <Helmet>
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      {GOOGLE_SITE_VERIFICATION ? (
        <meta name="google-site-verification" content={GOOGLE_SITE_VERIFICATION} />
      ) : null}

      {/* Open Graph */}
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:type" content={meta.ogType} />
      <meta property="og:url" content={getCanonicalUrl()} />
      <meta property="og:image" content={OG_IMAGE_URL} />
      <meta property="og:site_name" content="Neevii" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={OG_IMAGE_URL} />

      <link rel="canonical" href={getCanonicalUrl()} />

      {/* Structured Data (JSON-LD) */}
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}

